/**
 * PdfExportModal - Choose PDF export type (5 options)
 * Matches Curex 10_cong_cu/in_tom_tat_pdf.png
 *
 * Options:
 *   - In 2 Giải pháp (compare 2 plans)
 *   - In 3 Giải pháp (compare 3 plans)
 *   - Ước tính dòng tiền (cashflow table)
 *   - Bông hoa quyền lợi (flower visualization)
 *   - Tóm tắt quyền lợi (benefit summary)
 */
import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Alert, Platform } from 'react-native';
import { Icon } from './Icon';
import { colors, spacing, fontWeights, borderRadius } from '../theme';

export type PdfExportType =
  | 'compare-2'
  | 'compare-3'
  | 'cashflow'
  | 'flower'
  | 'benefits-summary';

export interface PdfExportModalProps {
  visible: boolean;
  onClose: () => void;
  customerName?: string;
  customerRole?: string;
  onExport: (type: PdfExportType) => Promise<void> | void;
}

const PDF_OPTIONS: Array<{
  type: PdfExportType;
  label: string;
  icon: any;
  description: string;
}> = [
  { type: 'compare-2', label: 'In 2 Giải pháp', icon: 'compare_arrows', description: 'So sánh 2 phương án cạnh nhau' },
  { type: 'compare-3', label: 'In 3 Giải pháp', icon: 'grid_view', description: 'So sánh 3 phương án' },
  { type: 'cashflow', label: 'Ước tính dòng tiền', icon: 'analytics', description: 'Bảng cashflow chi tiết 20 năm' },
  { type: 'flower', label: 'Bông hoa quyền lợi', icon: 'local_fire_department', description: 'Sơ đồ bông hoa 5 cánh' },
  { type: 'benefits-summary', label: 'Tóm tắt quyền lợi', icon: 'description', description: 'Tóm tắt ngắn gọn các quyền lợi' },
];

export const PdfExportModal: React.FC<PdfExportModalProps> = ({
  visible,
  onClose,
  customerName,
  customerRole = 'BH chính',
  onExport,
}) => {
  if (!visible) return null;

  const handleExport = async (type: PdfExportType) => {
    try {
      await onExport(type);
      Alert.alert(
        'Đang xuất PDF',
        `Đã bắt đầu xuất "${PDF_OPTIONS.find((o) => o.type === type)?.label}". File sẽ được tải xuống khi hoàn tất.`,
        [{ text: 'OK' }],
      );
      onClose();
    } catch (e) {
      Alert.alert('Lỗi', 'Không thể xuất PDF. Vui lòng thử lại.');
    }
  };

  return (
    <View style={styles.backdrop}>
      <TouchableOpacity
        style={StyleSheet.absoluteFill}
        activeOpacity={1}
        onPress={onClose}
      />
      <View style={styles.card}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>In tóm tắt, ước tính và so sánh giải pháp PDF</Text>
          <TouchableOpacity onPress={onClose}>
            <Icon name="close" size={22} color={colors.onSurfaceVariant} />
          </TouchableOpacity>
        </View>

        {/* Customer */}
        {customerName && (
          <View style={styles.customerRow}>
            <View style={styles.avatar}>
              <Icon name="person" size={20} color="#ffffff" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.customerName}>{customerName}</Text>
              <Text style={styles.customerRole}>{customerRole}</Text>
            </View>
          </View>
        )}

        {/* Options grid */}
        <View style={styles.optionsGrid}>
          {PDF_OPTIONS.map((opt) => (
            <TouchableOpacity
              key={opt.type}
              style={styles.optionBtn}
              onPress={() => handleExport(opt.type)}
            >
              <Icon name="description" size={18} color={colors.primary} />
              <Text style={styles.optionLabel}>{opt.label}</Text>
              <Text style={styles.optionDesc}>{opt.description}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    zIndex: 1000,
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    width: '100%',
    maxWidth: 640,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    gap: spacing[2],
  },
  title: {
    flex: 1,
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  customerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    padding: spacing[3],
    paddingHorizontal: spacing[4],
    backgroundColor: '#fafafa',
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#EF4444',
    justifyContent: 'center',
    alignItems: 'center',
  },
  customerName: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  customerRole: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  optionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[2],
    padding: spacing[4],
    justifyContent: 'space-between',
  },
  optionBtn: {
    width: '32%',
    minWidth: 140,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.md,
    padding: spacing[3],
    alignItems: 'center',
    gap: spacing[1],
  },
  optionLabel: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
    textAlign: 'center',
  },
  optionDesc: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    textAlign: 'center',
  },
});